import { useMemo, useState } from "react";
import useQualification from "@/module/education/api/useQualification";

export interface CourseFilterOption {
  value: string;
  label: string;
}

export interface CourseFilterConfig {
  key: string;
  label: string;
  options: CourseFilterOption[];
}

const ALL = "all";

const MATCH_OPTIONS: CourseFilterOption[] = [
  { value: ALL, label: "All Match" },
  { value: "exact", label: "Exact Match (90%+)" },
  { value: "close", label: "Close Match (70-89%)" },
  { value: "partial", label: "Partial Match (<70%)" },
];

const courseMatchBucket = (m: number | undefined | null) => {
  if (m == null || Number.isNaN(Number(m))) return null;
  const n = Number(m);
  if (n >= 90) return "exact";
  if (n >= 70) return "close";
  return "partial";
};

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const flattenFilterValues = (raw: unknown): string[] => {
  if (raw == null || raw === "") return [];
  if (Array.isArray(raw)) return raw.flatMap(flattenFilterValues);
  if (typeof raw === "object") {
    const record = raw as Record<string, unknown>;
    return flattenFilterValues(record.value ?? record.name ?? record.label);
  }
  return [String(raw)].map((v) => v.trim()).filter(Boolean);
};

const findCustomInputValues = (
  customInputs: Record<string, unknown> | undefined,
  targetKey: string
): string[] => {
  if (!customInputs || typeof customInputs !== "object") return [];
  const normalizedTarget = normalizeKey(targetKey);
  const matches: string[] = [];

  for (const [key, value] of Object.entries(customInputs)) {
    const normalizedKey = normalizeKey(key);
    if (
      normalizedKey === normalizedTarget ||
      normalizedKey.includes(normalizedTarget)
    ) {
      matches.push(...flattenFilterValues(value));
    }
  }

  return matches;
};

// Country lives on the university as a custom input ("country" key under
// university.customInputs). Multi-valued custom inputs surface as arrays;
// normalise to a flat string list so single + multi behave the same.
export const getCourseCountries = (course: any): string[] => {
  const values = [
    ...findCustomInputValues(course?.university?.customInputs, "country"),
    ...findCustomInputValues(course?.customInputs, "country"),
    ...flattenFilterValues(course?.university?.country),
    ...flattenFilterValues(course?.country),
  ];

  return Array.from(new Set(values));
};

const uniqueOptions = (
  values: Array<string | number | undefined | null>,
  formatter: (v: any) => CourseFilterOption | null = (v) => ({
    value: String(v),
    label: String(v),
  })
): CourseFilterOption[] => {
  const seen = new Set<string>();
  const out: CourseFilterOption[] = [];
  for (const v of values) {
    if (v == null || v === "") continue;
    const opt = formatter(v);
    if (!opt) continue;
    if (seen.has(opt.value)) continue;
    seen.add(opt.value);
    out.push(opt);
  }
  return out;
};

/**
 * Shared filter / search / eligibility state for the Course tab and
 * AttachCourse modal. Builds the dropdown options dynamically from the actual
 * course list so empty buckets don't show up. Keeps a single source of truth
 * for filtering logic so both surfaces behave identically.
 */
export default function useCourseFilters(courses: any[] | undefined) {
  const safeCourses = useMemo(() => courses ?? [], [courses]);
  const { levelSelects } = useQualification();

  const [search, setSearch] = useState("");
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [filters, setFilters] = useState<Record<string, string>>({
    country: ALL,
    university: ALL,
    courseType: ALL,
    match: ALL,
    level: ALL,
  });

  const setFilter = (key: string, value: string) =>
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      // COUNTRY → UNIVERSITY CASCADE: the university options below are built
      // from courses in the picked country, so a university outside it must
      // not linger as an invisible filter after the country changes.
      if (key === "country" && prev.university !== ALL) {
        const stillValid =
          value === ALL ||
          safeCourses.some(
            (c) =>
              String(c.universityId) === prev.university &&
              getCourseCountries(c).includes(value),
          );
        if (!stillValid) next.university = ALL;
      }
      return next;
    });

  const resetFilters = () => {
    setFilters({
      country: ALL,
      university: ALL,
      courseType: ALL,
      match: ALL,
      level: ALL,
    });
    setSearch("");
    setEligibleOnly(false);
  };

  const filterConfigs = useMemo<CourseFilterConfig[]>(() => {
    const countryOpts = uniqueOptions(
      safeCourses.flatMap((c) => getCourseCountries(c)),
      (v) => ({ value: String(v), label: String(v) })
    );
    // Universities are offered ONLY for the picked country: a course whose
    // university sits elsewhere can't be reached under that country anyway,
    // and listing it hands the user Lithuanian/French universities under "UK".
    const coursesInCountry =
      filters.country === ALL
        ? safeCourses
        : safeCourses.filter((c) => getCourseCountries(c).includes(filters.country));
    const universityOpts = uniqueOptions(
      coursesInCountry.map((c) =>
        c.universityId != null
          ? `${c.universityId}::${c.university?.name ?? "Untitled"}`
          : null
      ),
      (v) => {
        const [id, ...rest] = String(v).split("::");
        return { value: String(id), label: rest.join("::") || "Untitled" };
      }
    );
    const typeOpts = uniqueOptions(
      safeCourses.map((c) => c.courseType),
      (v) => ({ value: String(v), label: String(v) })
    );

    const levelIdsInUse = new Set(
      safeCourses
        .map((c) => c.qualificationLevelId ?? c.levelId)
        .filter((v) => v != null)
        .map((v) => String(v))
    );
    const levelOpts: CourseFilterOption[] = (levelSelects ?? [])
      .filter((l: any) => levelIdsInUse.has(String(l.value)))
      .map((l: any) => ({ value: String(l.value), label: l.name }));

    return [
      {
        key: "country",
        label: "Country",
        options: [{ value: ALL, label: "All Country" }, ...countryOpts],
      },
      {
        key: "university",
        label: "University",
        options: [{ value: ALL, label: "All Universities" }, ...universityOpts],
      },
      {
        key: "courseType",
        label: "Course Type",
        options: [{ value: ALL, label: "All Courses" }, ...typeOpts],
      },
      { key: "match", label: "Match", options: MATCH_OPTIONS },
      {
        key: "level",
        label: "Level",
        options: [{ value: ALL, label: "All Level" }, ...levelOpts],
      },
    ];
  }, [safeCourses, levelSelects, filters.country]);

  const filteredCourses = useMemo(() => {
    const q = search.trim().toLowerCase();

    return safeCourses.filter((c: any) => {
      if (eligibleOnly && !c.matchPercentage) return false;

      if (q) {
        const haystack = [c.name, c.university?.name]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      if (filters.country !== ALL) {
        const countries = getCourseCountries(c);
        if (!countries.includes(filters.country)) return false;
      }
      if (
        filters.university !== ALL &&
        String(c.universityId) !== filters.university
      ) {
        return false;
      }
      if (
        filters.courseType !== ALL &&
        String(c.courseType) !== filters.courseType
      ) {
        return false;
      }
      if (filters.match !== ALL) {
        const bucket = courseMatchBucket(c.matchPercentage);
        if (bucket !== filters.match) return false;
      }
      if (filters.level !== ALL) {
        const lvl = c.qualificationLevelId ?? c.levelId;
        if (String(lvl) !== filters.level) return false;
      }
      return true;
    });
  }, [safeCourses, search, eligibleOnly, filters]);

  return {
    search,
    setSearch,
    eligibleOnly,
    setEligibleOnly,
    filters,
    setFilter,
    resetFilters,
    filterConfigs,
    filteredCourses,
    totalCount: safeCourses.length,
    filteredCount: filteredCourses.length,
  };
}
